import * as React from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { flatten, isEqual } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { ManageLogo } from "@app/access-reports/admin/ManageLogo";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardLogo } from "@app/access-reports/shared/accessReportsWizardLogo";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";

interface AccessReportsAdminCoverPageTabProps {
  accessReportsWizard: AccessReportsWizardModel;
  disableUserActions: boolean;
  onCoverPageDefaultValueChange: (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  onCoverPageToggle: (elementId: string, key: keyof AccessReportsWizardElement) => void;
  onSaveClick: () => void;
  onSaveLogosClick: (logos: AccessReportsWizardOption[], reloadPage: boolean) => void;
  selectedVersion: AccessReportsWizardVersionModel;
}

interface CoverPageTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  defaultFieldName: string;
  defaultValue: JSX.Element;
  disable: JSX.Element;
  hide: JSX.Element;
  manageLogos: JSX.Element;
}

const columnConfig: ColumnConfig = {
  blank: {
    displayText: "",
    noSort: true,
    testId: "blank",
  },
  defaultFieldName: {
    displayText: "Default Field Name",
    noSort: true,
    testId: "default-field-name",
  },
  defaultValue: {
    displayText: "Default Value",
    noSort: true,
    testId: "default-value",
  },
  disable: {
    displayText: "Disable",
    noSort: true,
    testId: "disable",
  },
  hide: {
    displayText: "Hide",
    noSort: true,
    testId: "hide",
  },
  manageLogos: {
    displayText: "",
    noSort: true,
    testId: "manage-logos",
  }
};

const AccessReportsAdminCoverPageTab: React.FC<AccessReportsAdminCoverPageTabProps> = (props: AccessReportsAdminCoverPageTabProps) => {
  const getCoverPageElements = (template: AccessReportsWizardTemplate): AccessReportsWizardElement[] => {
    let coverPageElements: AccessReportsWizardElement[] = [];
    const coverPage: AccessReportsWizardPage = template.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

    if (!!coverPage) {
      for (const formGroup of coverPage.formGroups) {
        // remove product-related elements from this page
        const filteredElements: AccessReportsWizardElement[] = formGroup.elements.filter((x) => !x.elementId.includes("product"));
        coverPageElements = coverPageElements.concat(filteredElements);
      }
    }

    return coverPageElements;
  }

  const coverPageElements: AccessReportsWizardElement[] = getCoverPageElements(props.selectedVersion.template);

  const disableSave = (inProgressCoverPageElements: AccessReportsWizardElement[]): boolean => {
    const currentlySavedTemplate: AccessReportsWizardTemplate = props.accessReportsWizard.accessReportWizardVersions.find(
      x => x.accessReportWizardVersionId === props.selectedVersion.accessReportWizardVersionId
    )?.template;
    const currentlySavedCoverPageElements: AccessReportsWizardElement[] = getCoverPageElements(currentlySavedTemplate);

    if (isEqual(currentlySavedCoverPageElements, inProgressCoverPageElements) || props.disableUserActions) {
      return true;
    }

    return false;
  }

  const buildCoverPageTable = (coverPageElements: AccessReportsWizardElement[]): React.ReactNode => {
    const data: CoverPageTableData[] = coverPageElements.map((element, index) => ({
      /* oxlint-disable sort-keys */
      [wafflesKeyColumnName]: getTitle(element),
      defaultFieldName: getTitle(element),
      hide: (
        <QESCheckbox
          checked={element.hidden}
          className="m-1"
          disabled={props.disableUserActions}
          label=""
          testId={`access-reports-admin__cover-page--hide-checkbox-${index}`}
          onChange={() => props.onCoverPageToggle(element.elementId, "hidden")}
        />
      ),
      disable: (
        <QESCheckbox
          checked={element.disabled}
          className="m-1"
          disabled={props.disableUserActions}
          label=""
          testId={`access-reports-admin__cover-page--disable-checkbox-${index}`}
          onChange={() => props.onCoverPageToggle(element.elementId, "disabled")}
        />
      ),
      defaultValue: (
        element.elementType === AccessReportsWizardElementTypeEnum.Dropdown ? (
          <QESSelect
            allowEmpty={true}
            disabled={props.disableUserActions}
            testId={`access-reports-admin__cover-page--dropdown-${index}`}
            hideLabel={true}
            label={element.title}
            onChange={(e) => props.onCoverPageDefaultValueChange(element.elementId, e)}
            value={(element.defaultValue?.toString()) || ""}
          >
            {element.options.map((option, optionIndex) => (
              <option
                key={optionIndex}
                value={option.id}
              >
                {option.displayName ? option.displayName : option.name}
              </option>
            ))}
          </QESSelect>
        ) : (
          <QESTextInput
            testId={`access-reports-admin__cover-page--input-${index}`}
            disabled={props.disableUserActions || element.elementId === AccessReportsCoverPageElementEnum.To}
            hideLabel={true}
            label={element.title}
            onChange={(e) => props.onCoverPageDefaultValueChange(element.elementId, e)}
            value={element.defaultValue as string || ""}
          />
        )
      ),
      manageLogos: (
        (element.elementId === "logo") ? (
          <FlyoutLink
            classNames="qes-button-secondary"
            content={{
              body: renderManageLogosSidePanel(),
              title: `${props.disableUserActions ? "View" : "Manage"} Logos`,
            }}
            panelId="access-reports-admin__products--manage-logos"
            size="20"
            testId="access-reports-admin__products--manage-logos-link"
          >
            {props.disableUserActions ? "View" : "Manage"} Logos
          </FlyoutLink>
        ) : (
          <>&nbsp;</>
        )
      ),
      blank: <>&nbsp;</>,
      /* oxlint-enable */
    }));
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<CoverPageTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          fullWidth={true}
          testId={"access-reports-admin-cover-page-tab"}
        />
      </div>
    );
  }

  const renderManageLogosSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = props.selectedVersion.template;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const logoElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === "logo"
    );

    return (
      <ManageLogo
        disableUserActions={props.disableUserActions}
        onSaveLogosClick={props.onSaveLogosClick}
        logos={logoElement.options as AccessReportsWizardLogo[]}
      />
    );
  }

  const getTitle = (element: AccessReportsWizardElement): string => {
    if (!!element.title) {
      return element.title;
    } else {
      const splitKey: string = element.elementId.replace(/([A-Z])/g, " $1");
      const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

      return formattedKey;
    }
  }

  return (
    <div className="tab-pane fade" id="cover-page">
      <div className="row">
        <div className="col-12">
          <QESButton
            className="float-end mb-3"
            disabled={disableSave(coverPageElements)}
            qesType="primary"
            qesTestId={"access-reports-admin__dashboard--cover-page-save"}
            onClick={() => props.onSaveClick()}
          >
            Save
          </QESButton>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {buildCoverPageTable(coverPageElements)}
        </div>
      </div>
    </div>
  );
}

export { AccessReportsAdminCoverPageTab };
